import { Cesium } from 'senf-atomic-design-system';
import { useEffect, useRef } from 'react';

function MainPage() {
  const performanceMonitorRef = useRef<number>();

  useEffect(() => {
    // Enhanced Performance monitoring
    const logPerformance = () => {
      const memory = (performance as any).memory;
      if (memory) {
        console.log('🧠 Memory Usage:', {
          used: `${Math.round(memory.usedJSHeapSize / 1024 / 1024)} MB`,
          total: `${Math.round(memory.totalJSHeapSize / 1024 / 1024)} MB`,
          limit: `${Math.round(memory.jsHeapSizeLimit / 1024 / 1024)} MB`,
          percentUsed: `${Math.round((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100)}%`
        });
      }

      // Frame rate monitoring
      const now = performance.now();
      if (performanceMonitorRef.current) {
        const delta = now - performanceMonitorRef.current;
        const fps = Math.round(1000 / delta);
        console.log('📊 FPS:', fps);
      }
      performanceMonitorRef.current = now;

      // DOM node count (can indicate memory leaks)
      const domNodes = document.querySelectorAll('*').length;
      console.log('🏗️ DOM Nodes:', domNodes);

      // Event listener count estimation
      const eventListenerCount = (window as any).getEventListeners ? 
        Object.keys((window as any).getEventListeners(document)).length : 'N/A';
      console.log('👂 Event Listeners on Document:', eventListenerCount);
    };

    // WebGL/VRAM monitoring
    const checkVRAM = () => {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl') as WebGLRenderingContext | null;
      
      if (gl) {
        console.log('🎮 WebGL Info:', {
          vendor: gl.getParameter(gl.VENDOR),
          renderer: gl.getParameter(gl.RENDERER),
          version: gl.getParameter(gl.VERSION),
          maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
          maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE)
        });

        // Check for WebGL extensions that provide memory info
        const memoryInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (memoryInfo) {
          console.log('🖥️ GPU Info:', {
            vendor: gl.getParameter(memoryInfo.UNMASKED_VENDOR_WEBGL),
            renderer: gl.getParameter(memoryInfo.UNMASKED_RENDERER_WEBGL)
          });
        }

        // Try to get memory extension
        const memoryExtension = gl.getExtension('WEBGL_lose_context');
        console.log('🔌 WebGL Extensions Available:', gl.getSupportedExtensions());
      }
    };

    // Resource timing analysis
    const analyzeResourceTiming = () => {
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      const slowResources = resources
        .filter(resource => resource.duration > 100) // Resources taking more than 100ms
        .sort((a, b) => b.duration - a.duration)
        .slice(0, 10); // Top 10 slowest

      if (slowResources.length > 0) {
        console.group('🐌 Slow Resources (>100ms)');
        slowResources.forEach(resource => {
          console.log(`${Math.round(resource.duration)}ms - ${resource.name.split('/').pop()}`);
        });
        console.groupEnd();
      }

      // Network timing breakdown
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        console.log('🌐 Page Load Timing:', {
          dns: `${Math.round(navigation.domainLookupEnd - navigation.domainLookupStart)}ms`,
          connect: `${Math.round(navigation.connectEnd - navigation.connectStart)}ms`,
          request: `${Math.round(navigation.responseStart - navigation.requestStart)}ms`,
          response: `${Math.round(navigation.responseEnd - navigation.responseStart)}ms`,
          domProcessing: `${Math.round(navigation.domContentLoadedEventEnd - navigation.responseEnd)}ms`,
          totalLoad: `${Math.round(navigation.loadEventEnd - navigation.fetchStart)}ms`
        });
      }
    };

    // Component render timing
    const measureRenderTime = () => {
      performance.mark('render-start');
      requestAnimationFrame(() => {
        performance.mark('render-end');
        performance.measure('render-duration', 'render-start', 'render-end');
        const renderMeasure = performance.getEntriesByName('render-duration')[0];
        if (renderMeasure && renderMeasure.duration > 16.67) { // Longer than 1 frame at 60fps
          console.warn(`⏱️ Slow Render: ${Math.round(renderMeasure.duration)}ms (should be <16.67ms for 60fps)`);
        }
        performance.clearMarks();
        performance.clearMeasures();
      });
    };

    // CPU usage estimation
    const estimateCPUUsage = () => {
      const start = performance.now();
      let iterations = 0;
      const testDuration = 10; // 10ms test window
      
      while (performance.now() - start < testDuration) {
        iterations++;
      }
      
      const end = performance.now();
      const actualDuration = end - start;
      const efficiency = testDuration / actualDuration;
      const estimatedCPUUsage = Math.round((1 - efficiency) * 100);
      
      console.log('💻 Estimated CPU Load:', `${estimatedCPUUsage}%`);
    };

    // Initial checks
    logPerformance();
    checkVRAM();
    analyzeResourceTiming();
    measureRenderTime();
    estimateCPUUsage();

    // Set up periodic monitoring
    const performanceInterval = setInterval(() => {
      logPerformance();
      measureRenderTime();
      estimateCPUUsage();
    }, 5000); // Every 5 seconds
    
    const vramInterval = setInterval(checkVRAM, 30000); // Every 30 seconds
    const resourceInterval = setInterval(analyzeResourceTiming, 15000); // Every 15 seconds

    // Performance Observer for long tasks
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.duration > 50) { // Log tasks longer than 50ms
            console.warn('⚠️ Long Task Detected:', {
              name: entry.name,
              duration: `${Math.round(entry.duration)}ms`,
              startTime: Math.round(entry.startTime)
            });
          }
        });
      });
      
      try {
        observer.observe({ entryTypes: ['longtask', 'measure', 'navigation'] });
      } catch (e) {
        console.log('Performance Observer not fully supported');
      }

      return () => {
        observer.disconnect();
        clearInterval(performanceInterval);
        clearInterval(vramInterval);
        clearInterval(resourceInterval);
      };
    }

    return () => {
      clearInterval(performanceInterval);
      clearInterval(vramInterval);
      clearInterval(resourceInterval);
    };
  }, []);

  return <div className='h-screen w-screen fixed top-0 left-0'>
    hello
    {/* <Cesium bimAssetIds={[3459522]}/> */}
    
    </div>;
}

export default MainPage; 